import React, { Fragment, useRef, useState, useMemo, useCallback } from 'react'
import { Field, Form, Formik } from 'formik'
import TextField from 'components/Common/TextField'

import { store } from 'reduxStore'
import { AccountFormField, AccountFormLayout } from 'components/Common/AccountFormLayout'
import PhoneField from 'components/Common/PhoneField'
import * as yup from 'yup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import api from 'api'
import IndustryInfo from './IndustyInfo'
import AlertSuccess from './AlertSuccess'
import { faFacebook, faTwitter, faInstagram, faLinkedin } from '@fortawesome/free-brands-svg-icons'
import ZendeskLink from 'components/Common/ZendeskLink'
import UserContent from './UserContent'
import TextArea from './TextArea'
import { useOnClickOutside } from 'hooks'
import BusinessFormControls from './BusinessFormControls'
import { Typography, Box, Grid } from '@mui/material'
import Button from 'components/Common/Button'
import { DISABLED_BUTTON, PRIMARY_BUTTON } from 'themes/theme'

const sx = {
  blockHeader: {
    marginBottom: theme => theme.spacing(2),
    marginTop: theme => theme.spacing(3),
    fontWeight: 'bold',
    fontSize: 19,
    color: '#0076c5',
  },
  textInputRoot: {
    marginTop: 30,
  },
  switcherCustom: {
    opacity: '0.4',
  },
  alert: {
    position: 'relative',
    right: 0,
    marginTop: 24,
    [theme => theme.breakpoints.down('xs')]: {
      right: '0',
    },
  },
  industryNameInputBox: {
    width: '100%',
  },
  industryInputRoot: {
    boxShadow: 'none',
    border: 'none',
  },
  industryInput: {
    border: '1px solid #0000003b',
    borderRadius: 6,
    height: 47,
    boxSizing: 'border-box',
    '&:hover': {
      border: '1px solid #25245F',
    },
    '&:focus': {
      border: '2px solid #06489c',
      borderRadius: 6,
    },
  },
  layoutWrapper: {
    justifyContent: 'left',
  },
  layoutColumn: {
    marginRight: 20,
  },
  hidden: {
    visibility: 'hidden',
    height: 0,
  },
  infoIcon: {
    position: 'absolute',
    top: '-2px',
    left: '80px',
  },
  gridItem: {
    padding: '0 30px',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '1px',
      height: '100%',
    },
    '&:last-child': {
      paddingRight: 0,
      '&::before': {
        content: 'none',
      },
    },
  },
}

const sxBusinessTitles = {
  categories: {
    position: 'absolute',
    top: 80,
    width: '100%',
    padding: 10,
    zIndex: 1000,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    overflowY: 'auto',
    maxHeight: 220,
    fontSize: '14px',
    marginTop: '10px',
    boxShadow: '4px 4px 15px rgba(10, 120, 205, 0.08)',
    borderRadius: '5px',
  },
  span: {
    padding: 8,
    cursor: 'pointer',
    display: 'block',
    color: '#66668F',
    borderRadius: '5px',
    '&:hover': {
      backgroundColor: '#F4F4F7',
    },
  },
  closed: {
    display: 'none',
  },
}

const BusinessTitle = ({ categories, setValue, dropToDefault, style, lastSelectedValue, setLastValue }) => {
  const ref = useRef(null)
  useOnClickOutside(ref, () => {
    dropToDefault([])
    setValue('title', lastSelectedValue)
  })

  const action = item => {
    dropToDefault([])
    setLastValue && setLastValue(item)
    setValue('title', item)
  }

  if (categories.length === 0) return null

  return (
    <Box ref={ref} sx={sxBusinessTitles.categories} style={style}>
      {categories.map(item => (
        <Box component={"span"} onClick={() => action(item)} sx={sxBusinessTitles.span} key={item}>
          {item}
        </Box>
      ))}
    </Box>
  )
}

const schema = yup.object({
  socialFacebook: yup.string().notRequired(),
  socialTwitter: yup.string().notRequired(),
  socialLinkedIn: yup.string().notRequired(),
  socialInstagram: yup.string().notRequired(),
  category: yup.string().notRequired().default(''),
  businessId: yup.string().required(),
  businessDescription: yup.string(),
  firstName: yup.string(),
  lastName: yup.string(),
  phone: yup.string(),
  email: yup.string(),
  title: yup.string(),
  establishedYear: yup.number().max(new Date().getFullYear()).min(1600),
})

const emptyValue = {
  socialFacebook: '',
  socialTwitter: '',
  socialLinkedIn: '',
  socialInstagram: '',
  category: '',
  businessId: '',
  businessDescription: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  title: '',
  establishedYear: '',
}

const Info = ({ model, setBusiness, disableFormProp = false }) => {
  const user = store.getState().userReducer.user
  const formik = useRef(null)
  const [lastCategory, setLastCategory] = useState('')
  const [categories, setCategories] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const [businessTitles, setBusinessTitles] = useState([])
  const [lastTitle, setLastTitle] = useState('')
  const isFreeUser = user?.accountType === 'free'
  const pcPhone = model?.details?.primaryContact?.phone
  const maskPhone = pcPhone ? pcPhone.slice(0, 3) + -+pcPhone.slice(3, 6) + -+pcPhone.slice(6, 10) : ''

  const initialValues = useMemo(() => {
    if (model === null) return emptyValue
    setLastCategory(model.details.category || model.sic || '')
    return {
      socialFacebook: model.details.socialFacebook,
      socialTwitter: model.details.socialTwitter,
      socialLinkedIn: model.details.socialLinkedIn,
      socialInstagram: model.details.socialInstagram,
      category: model.details.category || model.sic || '',
      businessId: model.details.businessId,
      businessDescription: model.details.businessDescription,
      firstName: model.details.primaryContact.firstName,
      lastName: model.details.primaryContact.lastName,
      phone: maskPhone,
      email: model.details.primaryContact.email,
      title: model.details.primaryContact.title,
      establishedYear: model.details.establishedYear,
    }
  }, [model, maskPhone])

  const fetchChartData = async () => {
    await api.getDashboardInfo()
  }

  const onFormSubmit = useCallback(
    async (values, formikHelpers) => {
      formikHelpers.setSubmitting(true)
      setCategories([])

      const body = {
        ...initialValues,
        socialFacebook: values.socialFacebook,
        socialTwitter: values.socialTwitter,
        socialLinkedIn: values.socialLinkedIn,
        socialInstagram: values.socialInstagram,
        category: values.category,
        businessDescription: values.businessDescription,
        establishedYear: values.establishedYear,
        primaryContact: {
          firstName: values.firstName,
          lastName: values.lastName,
          title: values.title,
          phone: values.phone.replaceAll('-', ''),
          email: values.email,
        },
      }

      try {
        await api.updateMyBusinessInfo(body)
        const updatedBusiness = model && (await api.getUsersBusinessByExtId(model.ext_id))
        setBusiness(updatedBusiness)
        setSubmitted(true)
        setTimeout(fetchChartData, 2000)
      } catch (error) {
        console.log(error)
      } finally {
        formikHelpers.setSubmitting(false)
      }
    },
    [model, setBusiness, initialValues],
  )

  const getCategories = async search => {
    const res = await api.getBusinessCategories(search)
    setCategories(res.categories)
  }

  const getCompanyTitles = async search => {
    const { titles } = await api.getCompanyTitles(search)
    setBusinessTitles(titles)
  }

  const handleKeyPress = e => {
    var regex = new RegExp('^[0-9]+$')
    var key = String.fromCharCode(!e.charCode ? e.which : e.charCode)
    if (!regex.test(key)) {
      e.preventDefault()
      return false
    }
  }

  if (user?.kybValidated === undefined) {
    return (
      <UserContent
        label={user.accountType === 'free' ? 'Subscribe' : 'Find Now'}
        content={
          user.accountType === 'free'
            ? 'Become a premium member to claim your business, add additional fields on your profile and monitor businesses you are interested in.'
            : 'Find your business to claim it, add additional fields and enhance your profile for suppliers, customers and partners.'
        }
      />
    )
  }

  return (
    <>
      <Formik
        innerRef={formik}
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={onFormSubmit}
        enableReinitialize
      >
        {({ dirty, isSubmitting, setFieldValue }) => (
          <Form style={{ flex: 1 }}>
            <BusinessFormControls user={user} model={model} dirty={dirty} isSubmitting={isSubmitting} />
            <AccountFormLayout
              extraClasses={{ extraWrapper: sx.layoutWrapper, extraColumn: sx.layoutColumn }}
              rightSideFullSize
              leftSide={
                <Fragment>
                  <Box position="relative" className="customAuthInput">
                    <AccountFormField>
                      <Field name="category">
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            label="Industry"
                            placeholder="Industry"
                            fullWidth
                            borderLess
                            error={Boolean(meta.touched && meta.error)}
                            helperText={meta.touched && meta.error}
                            onChange={e => {
                              if (!e) {
                                getCategories('')
                                setFieldValue('category', '')
                              }
                              getCategories(e.target.value)
                              setFieldValue('category', e.target.value)
                              setTimeout(fetchChartData, 2000)
                            }}
                            onFocus={() => {
                              getCategories('')
                              setFieldValue('category', '')
                            }}
                            disabled={isFreeUser}
                          />
                        )}
                      </Field>
                    </AccountFormField>

                    <IndustryInfo
                      setValue={setFieldValue}
                      dropToDefault={setCategories}
                      categories={categories}
                      lastSelectedValue={lastCategory}
                      setLastValue={setLastCategory}
                    />

                    <div>
                      <Field name="establishedYear">
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            label="Established"
                            fullWidth
                            borderLess
                            rows="8"
                            onKeyPress={handleKeyPress}
                            error={Boolean(meta.touched && meta.error)}
                            helperText={meta.touched && meta.error ? 'Please enter valid year.' : ''}
                            disabled={isFreeUser}
                          />
                        )}
                      </Field>
                    </div>

                    <div>
                      <Field name="businessDescription">
                        {({ field }) => (
                          <TextArea
                            {...field}
                            style={{ marginBottom: 30 }}
                            rows="6"
                            label="Company Description"
                            disabled={isFreeUser}
                          />
                        )}
                      </Field>
                    </div>
                  </Box>
                  <div style={{ marginTop: '20px' }}>
                    <Typography sx={{ fontWeight: 'bold', marginBottom: '20px', fontSize: '13px', color: '#25245F' }}>
                      Primary Contact
                    </Typography>
                  </div>
                  <Grid container spacing={1} style={{ marginTop: 5 }} className="customAuthInput">
                    <Grid item xs={6}>
                      <AccountFormField style={{ height: 75, marginBottom: '20px' }}>
                        <Field name="firstName">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="First Name"
                              fullWidth
                              borderLess
                              rows="8"
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                    </Grid>
                    <Grid item xs={6}>
                      <AccountFormField style={{ height: 75, marginBottom: '20px' }}>
                        <Field name="lastName">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              style={{ marginBottom: 30 }}
                              label="Last Name"
                              fullWidth
                              borderLess
                              error={Boolean(meta.touched && meta.error)}
                              helperText={meta.touched && meta.error}
                              disabled={isFreeUser}
                            />
                          )}
                        </Field>
                      </AccountFormField>
                    </Grid>
                    <Grid item xs={12}>
                      <Box position="relative">
                        <AccountFormField style={{ height: 75, marginBottom: '20px' }}>
                          <Field name="title">
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                label="Title"
                                fullWidth
                                borderLess
                                error={Boolean(meta.touched && meta.error)}
                                helperText={meta.touched && meta.error}
                                isSelect
                                onClickSelect={() => {
                                  getCompanyTitles('')
                                }}
                                onChange={e => {
                                  if (!e) {
                                    getCompanyTitles('')
                                    setFieldValue('title', '')
                                  }
                                  getCompanyTitles(e.target.value)
                                  setFieldValue('title', e.target.value)
                                }}
                                onFocus={() => {
                                  getCompanyTitles('')
                                  setFieldValue('title', '')
                                }}
                                disabled={isFreeUser}
                              />
                            )}
                          </Field>
                          <BusinessTitle
                            setValue={setFieldValue}
                            dropToDefault={setBusinessTitles}
                            categories={businessTitles}
                            lastSelectedValue={lastTitle}
                            setLastValue={setLastTitle}
                          />
                        </AccountFormField>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className="customAuthInput">
                    <AccountFormField style={{ height: 85, marginBottom: '20px' }}>
                      <Field name="phone">
                        {({ field, meta }) => (
                          <PhoneField {...field} fullWidth isMasked label="Phone Number" disabled={isFreeUser} />
                        )}
                      </Field>
                    </AccountFormField>
                  </Grid>
                  <Grid item xs={12} className="customAuthInput">
                    <AccountFormField style={{ height: 75, marginBottom: '20px' }}>
                      <Field name="email">
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            label="Email"
                            fullWidth
                            borderLess
                            error={Boolean(meta.error)}
                            helperText={meta.error}
                            placeholder="example@example.com"
                            disabled={isFreeUser}
                          />
                        )}
                      </Field>
                    </AccountFormField>
                  </Grid>
                </Fragment>
              }
              center={<Grid className="verticalLine"></Grid>}
              rightSide={
                <Fragment>
                  <Typography sx={{ fontWeight: 'bold', marginBottom: '20px', fontSize: '13px', color: '#25245F' }}>
                    {' '}
                    Social Media
                  </Typography>
                  <AccountFormField>
                    <Box position="relative">
                      <FontAwesomeIcon
                        size="2x"
                        color="#25245F"
                        icon={faFacebook}
                        style={{ position: 'absolute', top: '25%', left: '-30px' }}
                      />

                      <Field name="socialFacebook">
                        {({ field, meta }) => <TextField {...field} fullWidth borderLess disabled={isFreeUser} />}
                      </Field>
                    </Box>
                  </AccountFormField>

                  <AccountFormField>
                    <Box position="relative">
                      <FontAwesomeIcon
                        size="2x"
                        color="#25245F"
                        icon={faTwitter}
                        style={{ position: 'absolute', top: '25%', left: '-30px' }}
                      />

                      <Field name="socialTwitter">
                        {({ field, meta }) => <TextField {...field} fullWidth borderLess disabled={isFreeUser} />}
                      </Field>
                    </Box>
                  </AccountFormField>

                  <AccountFormField>
                    <Box position="relative">
                      <FontAwesomeIcon
                        size="2x"
                        color="#25245F"
                        icon={faLinkedin}
                        style={{ position: 'absolute', top: '25%', left: '-30px' }}
                      />

                      <Field name="socialLinkedIn">
                        {({ field, meta }) => <TextField {...field} fullWidth borderLess disabled={isFreeUser} />}
                      </Field>
                    </Box>
                  </AccountFormField>
                  <AccountFormField>
                    <Box position="relative">
                      <FontAwesomeIcon
                        size="2x"
                        color="#25245F"
                        icon={faInstagram}
                        style={{ position: 'absolute', top: '25%', left: '-30px' }}
                      />

                      <Field name="socialInstagram">
                        {({ field, meta }) => <TextField {...field} fullWidth borderLess disabled={isFreeUser} />}
                      </Field>
                    </Box>
                  </AccountFormField>

                  <Box display="flex" justifyContent="center">
                    <Button
                      submit
                      loading={isSubmitting}
                      type={!dirty ? DISABLED_BUTTON : PRIMARY_BUTTON}
                      sx={{
                        width: '50%',
                      }}
                    >
                      Update
                    </Button>
                  </Box>
                  <AlertSuccess submitted={submitted} />
                </Fragment>
              }
            />
          </Form>
        )}
      </Formik>
      <div>
        <ZendeskLink />
      </div>
    </>
  )
}

export default Info
